import { useMutation } from "@apollo/react-hooks";
import Loader from "../../../components/Loader";
import { T } from '../../../utils/I18n/I18n';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { CREATE_ADDRESS } from '../../../components/PersonalArea/PersonalAreaAddress/queriesAddress';
import FormInput from '../../../components/UI/FormInput/FormInput';
import '../../../components/UI/FormInput/FormInput.scss';
import useSetBillingAddress from '../../../hooks/useSetBillingAddress';
import { GET_CUSTOMER } from '../../../queries/customer';
import { countryCode } from '../../../utils/checkout';
import isVATValid from '../../../utils/isVATValid';
import CountryOptions from '../CountryOptions';
import PostcodeAndCity from './SelectCityByPostcode/PostcodeAndCity';

const NewBillingAddressForm = ({
  selectedAddress,
  setSelectedAddress,
  billingAddress,
  setShowAddNewAddress,
  setShowAddressList,
  setBillingType,
  isBusiness,
}) => {
  const { register, handleSubmit, errors, setValue, watch } = useForm();

  const [showLoader, setShowLoader] = useState(false);

  const [createAddress] = useMutation(CREATE_ADDRESS, {
    refetchQueries: [{ query: GET_CUSTOMER }],
    awaitRefetchQueries: true,
  });

  const { setBillingAddress } = useSetBillingAddress();

  const submitHandler = async formData => {
    const { country, save, ...address } = formData;

    try {
      setShowLoader(true);

      if (save) {
        await createAddress({ variables: address });
      }

      await setBillingAddress({ address });

      setShowAddNewAddress(false);
      setShowAddressList(false);
      setSelectedAddress('addAddress');
      window.scrollTo(0, 0);
    } catch (err) {
      console.error(err);
    } finally {
      setShowLoader(false);
    }
  };

  const onExitHandler = () => {
    setShowAddNewAddress(false);
    setShowAddressList(false);
    if (billingAddress && billingAddress.company) {
      setBillingType(2); // TYPE_BUSINESS
    } else {
      setBillingType(1); // TYPE_PRIVATE
    }
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    if (selectedAddress === 'addAddress' && billingAddress) {
      if (isBusiness) {
        setValue('company', billingAddress.company);
        setValue('vat_id', billingAddress.vat_id);
      }

      setValue('firstname', billingAddress.firstname);
      setValue('lastname', billingAddress.lastname);
      setValue('street', billingAddress.street[0]);
      setValue('postcode', billingAddress.postcode);
      setValue('city', billingAddress.city);
      setValue('country_code', billingAddress.country.code);
      setValue('telephone', billingAddress.telephone);
    }
  }, [isBusiness]);

  return (
    <div className="checkout-address-item__new-address bottom-none">
      <form onSubmit={handleSubmit(submitHandler)}>
        {isBusiness && (
          <>
            <FormInput register={register} type="text" text="Ime tvrtke" name="company" errors={errors} />
            <FormInput
              refProp={register({ required: true, validate: { oib: isVATValid } })}
              type="text"
              text={<T id="address.vatId" />}
              name="vat_id"
              errors={errors}
            />
          </>
        )}

        <FormInput register={register} type="text" text="Ime" name="firstname" errors={errors} />
        <FormInput register={register} type="text" text="Prezime" name="lastname" errors={errors} />
        <FormInput
          register={register}
          type="text"
          text={<T id="address.street" />}
          minLength={3}
          name="street"
          errors={errors}
        />

        <PostcodeAndCity {...{ register, errors, watch, setValue }} />

        <FormInput
          register={register}
          errors={errors}
          text={<T id="address.country" />}
          name="country_code"
          onChange={() => setValue([{ city: '' }, { postcode: '' }])}
        >
          {countryCode === 'HR' && <option value="HR">Hrvatska</option>}
          {countryCode === 'RS' && <option value="RS">Srbija</option>}
          {countryCode === 'SI' && <option value="SI">Slovenija</option>}
          <CountryOptions onFetch={() => billingAddress && setValue('country_code', billingAddress.country.code)} />
        </FormInput>

        <FormInput register={register} type="number" text="Telefon" name="telephone" errors={errors} />

        <div className="checkout-address-item__new-address__checkbox">
          <FormInput
            register={register}
            type="checkbox"
            name="save"
            errors={errors}
            isCheckbox
          />{' '}
          <p>
            <T id="address.saveAddress" />
          </p>
        </div>

        {showLoader ? (
          <Loader height="xl" />
        ) : (
          <div className="checkout-address-item__new-address__buttons">
            {billingAddress && (
              <button className="checkout__main__form__button exit" onClick={onExitHandler} type="button">
                <T id="address.exit" />
              </button>
            )}

            <button className="checkout__main__form__button" type="submit">
              <T id="address.confirm" />
            </button>
          </div>
        )}
      </form>
    </div>
  );
};

export default NewBillingAddressForm;